/**
 * 고객 리포트의 추이 그래프 — **고객 영역 전용**이다 (260921-client-report-chart-only).
 * 운영자 화면은 계기판 성격의 RateTrend.tsx 를 그대로 쓴다.
 *
 * ★ 이 그래프의 주인공은 **95% 신뢰구간 띠**다. `wilson_lo/hi` 는 표본이 얇을수록 넓어지므로,
 *   띠는 클라이언트에서 통계를 한 줄도 새로 계산하지 않고 그 폭만으로 불확실성을 그린다.
 * ★ **선은 한 겹이고 점은 모두 같다.** 표본이 얇은 날을 옅은 선·빈 점으로 드러내던 표시는 걷어냈다 —
 *   고객에게 표본에 관한 정보를 보이지 않는다는 방침을 그림에도 적용한 것이다
 *   (2026-09-21, 260921-client-report-chart-only).
 * ★ 보간은 `monotone` 이다. 부드럽되 **측정값 밖의 값을 만들어내지 않는** 유일한 선택이다.
 * ★ 띠 가장자리에는 **세로 방향으로만** 블러를 먹인다. 신뢰구간의 경계는 절벽이 아니기 때문이고,
 *   가로를 흐리지 않으므로 날짜는 칼같이 선명하다. 블러는 `.recharts-area-area`(띠 채움)에만 걸리므로
 *   **불투명한 중심선은 애초에 대상이 아니다** — 선은 제자리에 또렷이 남는다.
 * ★ 마커까지 포함해 **색을 가진 것은 곡선 하나뿐**이다. 나머지는 전부 중성 슬레이트다.
 */
import type { Rate } from '@geo-admin/shared/contract/common.ts';
import { useId } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

/** 카드를 걷어낸 화면이라 표면은 흰색이 아니라 종이색이다 — 점의 링이 이 색을 쓴다 */
const PAPER = '#f8fafc';
const MUTED = '#94a3b8';

interface Point {
  date: string;
  attempts: number;
  mention: Rate;
}

const md = (iso: string) => `${Number(iso.slice(5, 7))}/${Number(iso.slice(8, 10))}`;

/** 첫 날·마지막 날을 반드시 포함해 5개 내외만 찍는다 — 90일이어도 축이 빽빽해지지 않게 */
function tickLabels(labels: string[]): string[] {
  if (labels.length <= 5) return labels;
  const step = (labels.length - 1) / 4;
  const idx = new Set([0, Math.round(step), Math.round(step * 2), Math.round(step * 3), labels.length - 1]);
  return [...idx].sort((a, b) => a - b).map((i) => labels[i] as string);
}

export function TrendArt({
  daily,
  markers,
  height = 148,
  from,
}: {
  daily: Point[];
  markers: { date: string; kind: string; value: string }[];
  height?: number;
  /** 이 날짜 이전(측정 시작 전)은 그리지 않는다. 같은 화면의 그래프들이 같은 값을 받아야 축이 맞는다 */
  from?: string;
}) {
  // 한 화면에 그래프가 여럿 뜬다 — SVG id 는 문서 전역이라 컴포넌트마다 갈라야 한다
  const uid = useId().replace(/:/g, '');
  const blurId = `soft-${uid}`;
  const wrapId = `trend-${uid}`;

  const pts = (from ? daily.filter((d) => d.date >= from) : daily).map((d) => {
    const { k, n, lo, hi } = d.mention;
    const rate = n > 0 ? (k / n) * 100 : null;
    return { label: md(d.date), rate, band: lo !== null && hi !== null ? [lo * 100, hi * 100] : null, n, k };
  });
  const last = [...pts].reverse().find((p) => p.rate !== null);
  // 한 건도 없으면 빈 축만 남아 고장처럼 보인다 — 축을 지우고 한 줄로 말한다
  if (pts.every((p) => p.n === 0))
    return (
      <div style={{ height }} className="flex w-full items-center text-slate-400 text-sm">
        아직 기록이 없습니다
      </div>
    );
  // 링을 90개까지 달면 구슬 목걸이가 된다. ★ 임계를 넘으면 점이 **통째로 사라지고 선만 남는다** —
  //   창이 정확히 days 개 점이므로(fillDaily) 실제로 걸리는 것은 90일 창뿐이다
  const ringed = pts.length <= 30;

  return (
    <div id={wrapId} style={{ height }} className="w-full">
      {/* 세로 전용 블러 — 경계만 녹이고 시간축은 흐리지 않는다 */}
      <style>{`#${wrapId} .recharts-area-area{filter:url(#${blurId})}`}</style>
      <svg width="0" height="0" className="absolute" aria-hidden>
        <title>필터 정의</title>
        <defs>
          <filter id={blurId} x="-2%" y="-25%" width="104%" height="150%">
            <feGaussianBlur stdDeviation="0 2.5" />
          </filter>
        </defs>
      </svg>
      <ResponsiveContainer>
        <ComposedChart data={pts} margin={{ top: 12, right: 20, bottom: 4, left: 0 }}>
          <CartesianGrid horizontal vertical={false} stroke="#eef2f7" strokeWidth={1} />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 50, 100]}
            axisLine={false}
            tickLine={false}
            width={38}
            tick={{ fontSize: 11, fill: MUTED }}
            tickFormatter={(v: number) => (v === 100 ? '100%' : String(v))}
          />
          <XAxis
            dataKey="label"
            ticks={tickLabels(pts.map((p) => p.label))}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: MUTED }}
            interval="preserveStartEnd"
          />
          {/*
            ★ 커스텀 content 를 쓰는 이유: 이 차트는 띠와 선을 함께 쌓으므로 기본 툴팁이 같은 값을
              **여러 줄** 나열한다. 한 점에 한 줄만 그린다.
            ★ 표본 수는 쓰지 않는다 — 고객에게 표본에 관한 정보를 보이지 않는다는 방침이다. 잰 날이
              하루도 없을 때만 `—` 다(0% 와 "안 쟀다" 는 다르다).
          */}
          <Tooltip
            cursor={{ stroke: '#cbd5e1', strokeWidth: 1 }}
            content={({ active, payload }) => {
              if (!active) return null;
              const d = payload?.[0]?.payload as (typeof pts)[number] | undefined;
              if (!d) return null;
              return (
                <div className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 text-xs shadow-sm">
                  <span className="text-slate-400">{d.label}</span> · {d.n === 0 ? '—' : `${Math.round(d.rate ?? 0)}%`}
                </div>
              );
            }}
          />
          {/* 문구가 바뀐 날 — 바닥에서 35% 까지만 오르는 1px 실선.
              ★ **중립색이다.** 운영자 화면(RateTrend)은 이 선을 주황(`--color-warn`)으로 그리지만 여기서는
                안 된다 — `toMarkersView` 가 비-admin 에게 `query_text` 만 내려보내므로 고객이 보는 마커는
                **전부** 그 색이 되고, "이 화면에서 색을 가진 것은 곡선 하나뿐" 이 그 자리에서 깨진다.
                ★ 글자도 범례도 달지 않는다. 고객이 뜻을 몰라도 되는 표시다 — 곡선이 왜 꺾였는지 물어올 때
                운영자가 `/admin` 질문 상세에서 답한다 */}
          {markers.map((m) => (
            <ReferenceLine
              key={`${m.date}-${m.kind}`}
              segment={[
                { x: md(m.date), y: 0 },
                { x: md(m.date), y: 35 },
              ]}
              stroke={MUTED}
              strokeOpacity={0.45}
              strokeWidth={1}
            />
          ))}
          <Area
            dataKey="band"
            type="monotone"
            stroke="none"
            fill="var(--color-brand)"
            fillOpacity={0.09}
            connectNulls={false}
            isAnimationActive={false}
          />
          <Line
            dataKey="rate"
            type="monotone"
            stroke="var(--color-brand)"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            connectNulls={false}
            isAnimationActive={false}
            dot={
              ringed
                ? (p: { cx?: number; cy?: number; index?: number; payload?: (typeof pts)[number] }) => {
                    const d = p.payload;
                    if (!d || d.rate === null || p.cx === undefined || p.cy === undefined) return <g key={p.index} />;
                    return (
                      <circle
                        key={p.index}
                        cx={p.cx}
                        cy={p.cy}
                        r={4}
                        fill="var(--color-brand)"
                        stroke={PAPER}
                        strokeWidth={2}
                      />
                    );
                  }
                : false
            }
          />
          {/* 마지막 날을 한 번 더 눌러 준다. **숫자는 달지 않는다** — 이 선의 직접 라벨은 같은 행
              왼쪽의 히어로 수치이고, 창 전체 값과 마지막 날 값을 나란히 두면 어긋난 것처럼 읽힌다 */}
          {last?.rate != null ? (
            <ReferenceDot
              x={last.label}
              y={last.rate}
              r={5}
              fill="var(--color-brand)"
              stroke={PAPER}
              strokeWidth={2}
              ifOverflow="extendDomain"
            />
          ) : null}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
